import { FC } from 'react';
import { createPortal } from 'react-dom';

import { Invoice } from '@typings/api-models';
import * as format from '@format';
import { m } from 'framer-motion';
import Info from '@components/Icons/Info';
import usePopover from '@/hooks/usePopover';
import s from './InvoiceProgressTooltip.module.scss';

type Props = {
  invoice: Pick<Invoice, 'amountPaid' | 'amountTotal' | 'amountPending' | 'amountDue' | 'currency'>;
  hideIcon?: boolean;
  hideProgress?: boolean;
  className?: string;
  title?: string;
  hideSuffix?: boolean;
};

const InvoiceProgressTooltip: FC<Props> = ({ invoice, hideIcon, hideProgress, className = '', title = 'Remaining Balance: ', hideSuffix = false }) => {
  const { isOpen, setIsOpen, menuRef, triggerRef } = usePopover('bottom-left');
  return (
    <div>
      <m.div
        ref={triggerRef as unknown as React.RefObject<HTMLDivElement>}
        className={s.progress}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <div className={s.info}>
          {!hideIcon && <Info weight="fill" size={12} color="var(--color-gray-100)" />}
          <p className={`caption ${className}`}>{title}{format.price.withCurrency(invoice.amountDue, invoice.currency, hideSuffix)}</p>
        </div>
        {
          !hideProgress && (
            <div className={s.bars}>
              <progress value={invoice.amountPending + invoice.amountPaid} max={invoice.amountTotal} />
              <progress value={invoice.amountPaid} max={invoice.amountTotal} />
            </div>
          )
        }
      </m.div>
      {isOpen && createPortal(
        <m.ul
          ref={menuRef as unknown as React.RefObject<HTMLUListElement>}
          className={s.tooltip}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 4 }}
        >
          <li>
            Invoice Total <b>{format.price.withCurrency(invoice.amountTotal, invoice.currency)}</b>
          </li>
          <li>
            {invoice.amountPaid > 0 || (invoice.amountPending === 0 && invoice.amountPaid === 0) ? 'Paid' : ''}
            {invoice.amountPaid > 0 && invoice.amountPending > 0 ? ' and ' : ''}
            {invoice.amountPending > 0 ? 'Pending' : ''}
            <b>{format.price.withCurrency(invoice.amountPaid + invoice.amountPending, invoice.currency)}</b>
          </li>
          <li>
            Remaining Balance <b>{format.price.withCurrency(invoice.amountDue, invoice.currency)}</b>
          </li>
        </m.ul>,
        document.body,
      )}
    </div>
  );
};

export default InvoiceProgressTooltip;
